import {
  Component,
  Input,
  Output,
  EventEmitter,
  QueryList,
  ViewChildren,
  ViewChild,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ElementRef,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Person,
  SearchFilter,
  SearchPersonsData200Response,
  SearchPersonsDataRequest,
} from 'ldt-people-api';
import { AuthService, UIOrg } from 'src/app/auth/service/auth.service';
import { ColumnCategory, ColumnType, DWColumn } from 'src/app/shared/dw-column';
import { FieldSelectorDialogComponent } from 'src/app/shared/field-selector-dialog/field-selector-dialog.component';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { PeopleColumns } from '../people-columns';
import { FilterGroupComponent } from './filter-group/filter-group.component';
import { AddQuickBuildModalComponent } from './add-quick-build-modal/add-quick-build-modal.component';
import {
  QuickBuild,
  QuickBuildService,
  UIFilter,
  UIFilterGroup,
  UniqueIdGenerator,
} from '../quick-build.service';

import { getPeopleFancyGridColumnDefs } from './people-ag-grid-fancy';
import { GridApi } from 'ag-grid-community';
import { GridReadyEvent, RowClickedEvent, ColDef, ColGroupDef } from 'ag-grid-community';
import { DarkModeService } from 'src/app/shared/dark-mode/dark-mode.service';
import { QuickBuildSchema } from '../quick-build-validators';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
// import { ChatService, ErrorRequest, SendSearchResultsRequest } from 'ldt-chat-api'; // TODO
import { ChatToQueryResult } from './chat-to-filters/chat-to-filters.component';
import { AlertType } from 'src/app/shared/alert/alert.component';
import { Log } from 'src/app/logger.service';
import { StreamsSubscribeDrawerService } from 'src/app/shared/streams-subscribe-drawer/streams-subscribe-drawer.service';
import { PersonProfileComponent } from 'src/app/shared/person-profile/person-profile.component';
import { defaultColDef } from '../people-full-grid-settings';
import { StoredItemsBrowserComponent } from 'src/app/shared/components/stored-items-browser/stored-items-browser.component';
import {
  QuickBuildStorageService,
  StoredQuickBuild,
} from 'src/app/core/services/quick-build-storage.service';
import { PeopleReportService } from '../report-service.service';
import { RoleDefinitionsService } from 'src/app/core/services/role-definitions.service';
import { HostnameService } from 'src/app/hostname-router-service';

// Filters larger than this (URL-encoded) stay out of the querystring and live only in
// localStorage. Analytics scripts compile location.href into a regex on every link click, so a
// multi-megabyte settings param makes that regex compile throw and the resulting error freezes
// the tab; URLs that size aren't shareable anyway.
const MAX_URL_FILTER_LENGTH = 2000;

@Component({
  selector: 'app-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  standalone: false,
})
export class FiltersComponent implements OnChanges, OnDestroy {
  @ViewChild('quickBuildMenuTrigger') quickBuildMenuTrigger: MatMenuTrigger;
  @ViewChild('filtersPanel', { static: false }) filtersPanel: ElementRef;

  @Input() embedded: boolean = false;
  @Input() setQuickBuild: QuickBuild | undefined;
  @Output() searchCountChange = new EventEmitter<number>();
  @Output() quickBuildChange = new EventEmitter<QuickBuild>();

  // For time-series based charts, the API returns data from the beginning of time. We filter it here.
  startDateDate: Date = new Date('2021-01-01');
  endDateDate: Date = new Date();
  reportTarget: string;

  isEmployee$: Observable<boolean>;
  orgSettings: Observable<UIOrg | undefined>;
  palMode: boolean = false;

  orgId: string;
  showFilters: boolean = true;
  refreshing: boolean = false;
  countUpdating: boolean = false;
  searchCount: number = 0;
  downloading: boolean = false;
  collapseQuickBuilds: boolean = true;
  apiResponse: SearchPersonsData200Response | null = null;

  columnDefs: (ColDef | ColGroupDef)[] = [];
  defaultColDef = defaultColDef;
  rowData: any[] = [];
  private samplePeopleGrid!: GridApi<any>;
  AlertType = AlertType;

  uiFilters: UIFilter[] = [];
  columns: DWColumn[] = PeopleColumns;

  categories: ColumnCategory[] = [
    {
      name: 'personal',
      displayName: 'Personal',
      description: 'Personal information',
      displayColumn: 1,
      subcategories: [
        {
          name: 'identity',
          displayName: 'Identity',
          description: 'Basic identifying information like name and ID',
          order: 1,
        },
        {
          name: 'location',
          displayName: 'Location',
          description: 'Geographic information about the person',
          order: 2,
        },
      ],
    },
    {
      name: 'jobs',
      displayName: 'Job Experience',
      description: 'Job characteristics, such as company, title, and dates of employment',
      displayColumn: 3,
      subcategories: [
        {
          name: 'company',
          displayName: 'Company Identifiers',
          description: 'Use for searching for specific companies',
          order: 1,
        },
        {
          name: 'firmo',
          displayName: 'Company Characteristics',
          description: 'Firmographic information about the company',
          order: 4,
        },
        {
          name: 'role',
          displayName: 'Role Information',
          description: 'Role characteristics, such as title, level, and function',
          order: 2,
        },
        {
          name: 'location',
          displayName: 'Job Location',
          description: 'Employees stated location of this particular job',
          order: 5,
        },
        {
          name: 'dates',
          displayName: 'Dates and Tenure',
          description: 'Start dates, end dates, and tenure information',
          order: 3,
        },
      ],
    },
    {
      name: 'education',
      displayName: 'Education',
      description: 'Information about education',
      displayColumn: 1,
    },
    {
      name: 'metadata',
      displayName: 'Timestamps',
      description: 'Timestamps for this record',
      displayColumn: 1,
    },
  ];

  rootFilterGroup: UIFilterGroup = {
    id: UniqueIdGenerator.generate(),
    operator: 'and',
    filters: [],
  };

  private quickBuildLoadSubscription?: Subscription;
  peopleGridType: 'sample' | 'full' = 'sample';
  loadedQuickBuild: StoredQuickBuild | null = null;

  constructor(
    private dialog: MatDialog,
    private notify: NotificationService,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    public darkModeService: DarkModeService,
    private drawerService: StreamsSubscribeDrawerService,
    private qbStorageService: QuickBuildStorageService,
    private reportService: PeopleReportService,
    private roleDefinitionsService: RoleDefinitionsService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    // When setQuickBuild input changes after initialization, reload and execute
    if (changes['setQuickBuild'] && !changes['setQuickBuild'].firstChange) {
      if (this.setQuickBuild) {
        this.loadFiltersFromInput();
      }
    }
  }

  ngOnInit() {
    // Access navigation state via history.state
    const navigationState = history.state as {
      al_config?: any;
      al_created_at?: string;
      quickBuild?: QuickBuild;
      fromChat?: boolean;
      threadId?: string;
    };

    // Try to load page state if someone is coming from an access link
    if (navigationState?.al_config && navigationState.al_config.quickBuild) {
      this.loadQuickBuild(navigationState.al_config.quickBuild, true);
    }

    // Handle PAL mode. If someone exits PAL mode, the org subscription below will take care of things
    if (this.authService.palModeValue) {
      this.palMode = true;
      this.setGridType(undefined);
      this.showFilters = true;

      return;
    }

    if (navigationState?.fromChat && navigationState?.quickBuild) {
      Log.d('Loading quickbuild from chat:', navigationState.quickBuild);
      // Store the quickbuild to be loaded after initialization
      this.setQuickBuild = navigationState.quickBuild;

      // Clear navigation state
      history.replaceState({}, '', window.location.pathname);
    }

    // In embedded mode, start with filters collapsed
    if (this.embedded) {
      this.showFilters = false;
    }

    this.isEmployee$ = this.authService.$isAdmin;
    this.orgSettings = this.authService.$getSelectedOrg;

    // Trigger early loading of quickbuilds and role definitions to avoid menu delay
    this.quickBuildLoadSubscription = this.qbStorageService.items$.pipe(take(1)).subscribe();
    this.quickBuildLoadSubscription.add(
      this.roleDefinitionsService.roleDefinitions$.pipe(take(1)).subscribe()
    );

    let orgId = this.route.parent?.snapshot.paramMap.get('orgId');
    if (!orgId) {
      orgId = this.authService.getSelectedOrgIdValue;
      if (!orgId) {
        this.notify.error('Invalid path');
        this.router.navigateByUrl('/main');
      }
    }
    this.orgId = orgId;
    this.setGridType(this.authService.getSelectedOrgValue);
    this.authService.$getSelectedOrg.subscribe((org) => {
      this.setGridType(org);
    });

    if (!this.loadFiltersFromInput()) {
      if (!this.loadFiltersFromQuerystring()) {
        this.loadFiltersFromLocalStorage();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.quickBuildLoadSubscription) {
      this.quickBuildLoadSubscription.unsubscribe();
    }
  }

  setGridType(org: UIOrg | undefined) {
    Log.d('app-filters setGridType org', org);
    this.peopleGridType = 'sample';
    if (!org) {
      return;
    }
    const peopleSettings = org?.settings?.people;
    if (peopleSettings?.subType === 'unlimited') {
      this.peopleGridType = 'full';
    }

    // Update column definitions when grid type changes
    if (this.samplePeopleGrid) {
      this.columnDefs = getPeopleFancyGridColumnDefs(this.peopleGridType);
      this.samplePeopleGrid.setGridOption('columnDefs', this.columnDefs);
    }
  }

  allReportIDsGrouped: { [key: string]: string[] } = {};
  loadFiltersFromInput(): boolean {
    let data = this.setQuickBuild;
    if (!data) {
      return false;
    }

    if (typeof data === 'string') {
      data = QuickBuildService.deserializeQuickBuild(data);
    }

    this.loadQuickBuild(data, true);

    // Use setTimeout to ensure view is initialized before running search
    setTimeout(() => {
      this.runSearch();
    }, 0);

    return true;
  }

  /**
   * Opens a modal dialog to select filter based on the given category, then updates filter arrays to add this filter.
   */
  showSelectorModal(isJobsGroup: boolean): Promise<string | undefined> {
    return new Promise((resolve) => {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.disableClose = true;
      dialogConfig.autoFocus = true;
      dialogConfig.width = '1200px';
      dialogConfig.height = '80%';

      try {
        // if isJobsGroup is true, only show filters from the 'jobs' category
        if (isJobsGroup) {
          const category = this.categories.filter((c) => c.name === 'jobs');
          const fields = this.columns.filter((c) => c.group === 'jobs');
          dialogConfig.data = {
            categories: category,
            fields: fields,
          };
        } else {
          dialogConfig.data = { categories: this.categories, fields: this.columns };
        }
        const dialogRef = this.dialog.open(FieldSelectorDialogComponent, dialogConfig);
        dialogRef.afterClosed().subscribe((data) => {
          resolve(data);
        });
      } catch (err) {
        Log.e('Error adding filter: ', err);
        this.notify.error('Error adding filter. Please try again later.');
        resolve(undefined);
      }
    });
  }

  /**
   * Handles data (single filter name) returned from the field selector dialog and adds the selected filter to the appropriate group.
   * @param filterName - filterName to be handled, e.g. 'position.company.name'.
   * @param groupComponent - the group component to which the filter should be added.
   */
  handleDataFromDialog(filterName: string, groupComponent: UIFilterGroup): void {
    const isRootGroup = groupComponent === this.rootFilterGroup;
    const foundFilter = this.columns.find((f) => f.name === filterName);

    if (foundFilter) {
      const newFilter: UIFilter = {
        filter: {
          field: foundFilter.name,
          type: SearchFilter.TypeEnum.Must,
          match_type: this.matchTypesAsObject(foundFilter.name)[0],
        },
      };

      // Check if the groupComponent is a 'jobs' group; if yes, add the filter directly to this group
      if (groupComponent.isJobsGroup) {
        groupComponent.filters.push(newFilter);
      } else {
        // Check if the filter belongs to the 'jobs' category
        const isPastJobsFilter = foundFilter.group === 'jobs';

        // Get the filters array based on the type of groupComponent
        const groupFilters = this.getFiltersArray(groupComponent);

        if (isPastJobsFilter) {
          // Check if there is an existing 'jobs' group within this group
          let pastJobsGroup = this.findPastJobsGroup(groupFilters);

          // if current group is empty, add new filter directly to this group and convert it to a 'jobs' group
          if (groupFilters.length === 0 && !isRootGroup) {
            groupFilters.push(newFilter);
            groupComponent.isJobsGroup = true;
            groupComponent.jobsGroupType = 'any';
            return;
          }

          pastJobsGroup = {
            id: UniqueIdGenerator.generate(),
            operator: 'and',
            filters: [newFilter],
            isJobsGroup: true,
          };
          groupFilters.push(pastJobsGroup);
        }

        // Add to the normal group
        else {
          groupComponent.filters.push(newFilter);
        }
      }
    } else {
      Log.e(`Filter ${filterName} does not exist.`);
    }
  }

  /**
   * Helper method to find an existing 'jobs' group in the given filters array.
   */
  private findPastJobsGroup(filters: Array<UIFilter | UIFilterGroup>): UIFilterGroup | undefined {
    return filters.find(
      (g) =>
        QuickBuildService.isUIFilterGroup(g) &&
        (g as UIFilterGroup).isJobsGroup &&
        g.operator === 'and' // Optional: Add more checks to ensure the correct group
    ) as UIFilterGroup;
  }

  /**
   * Helper method to get the filters array from a FilterGroup or FilterGroupComponent.
   */
  private getFiltersArray(
    groupComponent: FilterGroupComponent | UIFilterGroup
  ): Array<UIFilter | UIFilterGroup> {
    return groupComponent instanceof FilterGroupComponent
      ? groupComponent.group.filters
      : groupComponent.filters;
  }

  handleFilterAdded(event: { component: FilterGroupComponent | null; isJobsGroup: boolean }): void {
    if (!event.component) {
      // Handle the case where we're adding a filter from the empty state to the root group
      this.showSelectorModal(event.isJobsGroup).then((selectedFilterName: string | undefined) => {
        if (selectedFilterName) {
          this.handleDataFromDialog(selectedFilterName, this.rootFilterGroup);
        }
      });
      return;
    }

    // Open the filter selection dialog and pass the `isJobsGroup` value
    this.showSelectorModal(event.isJobsGroup).then((selectedFilterName: string | undefined) => {
      if (selectedFilterName) {
        this.handleDataFromDialog(selectedFilterName, event.component!.group);
      }
    });
  }

  addFilterGroup(): void {
    const newGroup: UIFilterGroup = {
      id: UniqueIdGenerator.generate(),
      operator: 'and',
      filters: [],
    };
    this.rootFilterGroup.filters.push(newGroup);
  }

  deleteAllFilters() {
    this.rootFilterGroup.filters = [];
    this.loadedQuickBuild = null;
    this.showFilters = true; // Expand the panel when clearing filters
  }

  // Updates the current URL querystring and browser cache so that the URL can be copied and shared with others and so
  // that navigating back to this page loads the same search. Uses the quickbuild serializer to get a string representation of the search params
  updateUrl(): void {
    if (this.embedded) return;

    // The id and name aren't needed in this case, so we just use placeholders
    const qb: QuickBuild = {
      id: UniqueIdGenerator.generate(),
      name: '',
      rootFilterGroup: this.rootFilterGroup,
    };
    const settings = QuickBuildService.serializeQuickBuild(qb);
    const encodedFilter = encodeURIComponent(settings);
    this.quickBuildChange.emit(qb);

    // Save to local storage and the URL querystring
    localStorage.setItem('person-filter-params', settings);

    if (encodedFilter && encodedFilter.length <= MAX_URL_FILTER_LENGTH) {
      // We have a reasonably-sized filter, ensure it's in the URL and correct
      if (this.route.snapshot.queryParams.settings !== encodedFilter) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { settings: encodedFilter },
          queryParamsHandling: 'merge',
        });
      }
    } else {
      // No filter (or one too large for the URL — localStorage still has it), ensure 'settings'
      // is not in the URL
      if (this.route.snapshot.queryParams.settings !== undefined) {
        const queryParams = { ...this.route.snapshot.queryParams };
        delete queryParams.settings;
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: queryParams,
        });
      }
    }
  }

  // Loads a serialized set of filters from the querystring into the page. If the querystring didn't exist or was malformed
  // this returns false
  loadFiltersFromQuerystring(): boolean {
    const { settings } = this.route.snapshot.queryParams;
    if (!settings) {
      return false;
    }

    try {
      const serializedQB: string = decodeURIComponent(settings);
      this.loadSerializedQuickBuild(serializedQB);
      this.collapseQuickBuilds = true;
      return true;
    } catch (error) {
      // Clear the querystring if it was malformed
      this.updateUrl();
    }

    return false;
  }

  // Loads a serialized set of filters from localstorage into the page, if they exist
  loadFiltersFromLocalStorage(): void {
    try {
      const savedParams: any = localStorage.getItem('person-filter-params');
      if (savedParams) {
        this.loadSerializedQuickBuild(savedParams);
      }
    } catch (error) {
      Log.e('Error getting data from localstorage:', error);
    }
  }

  // Loads a serialized quickbuild string into the page
  loadSerializedQuickBuild(serializedQuickBuild: string): void {
    const qb = QuickBuildService.deserializeQuickBuild(serializedQuickBuild);
    this.loadQuickBuild(qb);
  }

  lastChatResult: ChatToQueryResult | null = null;

  runSearch() {
    this.updateUrl();
    this.setLoading(true);
    this.showFilters = false;

    // Scroll to collapsed filters after view update
    setTimeout(() => {
      if (this.filtersPanel && this.filtersPanel.nativeElement) {
        this.filtersPanel.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);

    const [filters, aggs] = QuickBuildService.rootFilterGroupToAPIFilters(this.rootFilterGroup);
    if (this.setQuickBuild) {
      // Make a deep copy and replace the filters. Needed to trigger change detection
      const newQB = QuickBuildService.copyQuickBuild(this.setQuickBuild);
      newQB.rootFilterGroup = this.rootFilterGroup;
      this.reportTarget = newQB.rootFilterGroup.id || '';
      this.setQuickBuild = newQB;
    } else {
      this.setQuickBuild = {
        id: UniqueIdGenerator.generate(),
        name: 'Active Filters',
        rootFilterGroup: this.rootFilterGroup,
      };
      this.reportTarget = this.setQuickBuild.rootFilterGroup.id || '';
    }

    this.reportService
      .getPeopleData(this.setQuickBuild, this.peopleGridType === 'sample', undefined, 50, undefined)
      .subscribe({
        next: (data) => {
          this.apiResponse = data;
          this.searchCount = data.count || 0;
          this.searchCountChange.emit(this.searchCount);
          this.rowData = data.people || [];
          this.setLoading(false);
        },
        error: (err) => {
          this.setLoading(false);
          this.searchCount = 0;
          this.searchCountChange.emit(this.searchCount);
          if (err.status === 402) {
            return;
          }
          Log.e('Error searching for people:', err);
          this.notify.error('Error searching for people');
        },
      });
  }

  setLoading(loading: boolean) {
    if (loading) {
      this.refreshing = true;
      this.countUpdating = true;
      return;
    }

    this.refreshing = false;
    this.countUpdating = false;
  }

  onGridReady(params: GridReadyEvent<any>) {
    this.columnDefs = getPeopleFancyGridColumnDefs(this.peopleGridType);
    this.samplePeopleGrid = params.api;
  }

  download() {
    const qb: QuickBuild = {
      id: UniqueIdGenerator.generate(),
      name: 'Download',
      rootFilterGroup: this.rootFilterGroup,
    };
    this.drawerService.openDrawer({
      quickBuild: qb,
      mode: 'data-selection',
      matchingRecords: this.searchCount,
    });
  }

  copyFiltersToClipboard() {
    const [filters, aggs] = QuickBuildService.rootFilterGroupToAPIFilters(this.rootFilterGroup);

    const req: SearchPersonsDataRequest = {
      filters: filters.filters,
      size: 100,
    };

    const filtersString = JSON.stringify(req, null, 2);
    navigator.clipboard.writeText(filtersString).then(
      () => {
        this.notify.success('Filters copied to clipboard');
      },
      () => {
        this.notify.error('Error copying filters to clipboard');
      }
    );
  }

  copyQuickBuildToClipboard() {
    const qb: QuickBuild = {
      id: UniqueIdGenerator.generate(),
      name: 'Clipboard Quick Build',
      rootFilterGroup: this.rootFilterGroup,
    };

    const qbString = QuickBuildService.serializeQuickBuild(qb);
    navigator.clipboard.writeText(qbString).then(
      () => {
        this.notify.success('Quick build copied to clipboard');
      },
      () => {
        this.notify.error('Error copying quick build to clipboard');
      }
    );
  }

  copyApiResponseToClipboard() {
    const respString = JSON.stringify(this.apiResponse, null, 2);
    navigator.clipboard.writeText(respString).then(
      () => {
        this.notify.success('Response copied to clipboard');
      },
      () => {
        this.notify.error('Error copying response to clipboard');
      }
    );
  }

  importQBFromClipboard() {
    navigator.clipboard.readText().then(
      (text) => {
        try {
          const data = JSON.parse(text);
          const result = QuickBuildSchema.safeParse(data[0]);

          if (!result.success) {
            this.notify.error('Error importing quick build from clipboard');
            Log.d(result.error.toString());
          }

          this.loadQuickBuild(data as QuickBuild);
          this.notify.success('Filters imported from clipboard');
        } catch (error) {
          Log.e('Error parsing quick build from clipboard', error);
          this.notify.error('Error parsing quick build from clipboard');
        }
      },
      () => {
        this.notify.error('Error copying from clipboard');
      }
    );
  }

  onPersonRowClicked(event: RowClickedEvent) {
    if (event.data) {
      const currentIndex = this.samplePeopleGrid.getRowNode(event.data.id)?.rowIndex || 0;
      const rowData: Person[] = [];
      this.samplePeopleGrid.forEachNode((node) => {
        if (node.data) {
          rowData.push(node.data);
        }
      });

      this.dialog.open(PersonProfileComponent, {
        backdropClass: 'darker-dialog-backdrop',
        width: '95vw',
        height: '90vh',
        panelClass: 'person-profile-dialog',
        data: {
          person: event.data,
          people: rowData,
          currentIndex: currentIndex,
        },
      });
    }
  }

  showAddQuickBuildDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '500px';
    const dialogRef = this.dialog.open(AddQuickBuildModalComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        const qb: QuickBuild = {
          id: UniqueIdGenerator.generate(),
          name: data.name,
          rootFilterGroup: this.rootFilterGroup,
          description: data.description,
        };
        // Type assertion to StoredQuickBuild to include category
        const storedQb = {
          ...qb,
          category: data.category,
        };
        this.qbStorageService.create(storedQb).subscribe({
          next: () => {
            this.notify.success('Quick build created');
            // Set the newly created quickbuild as the loaded one
            this.loadedQuickBuild = storedQb;
          },
          error: () => {
            this.notify.error('Error creating quick build');
          },
        });
      }
    });
  }

  saveExistingQuickBuild() {
    if (!this.loadedQuickBuild) {
      return;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '500px';
    dialogConfig.data = {
      editMode: true,
      quickBuild: this.loadedQuickBuild,
    };

    const dialogRef = this.dialog.open(AddQuickBuildModalComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((data) => {
      if (data && this.loadedQuickBuild) {
        const updatedQb: StoredQuickBuild = {
          ...this.loadedQuickBuild,
          name: data.name,
          rootFilterGroup: this.rootFilterGroup,
          description: data.description,
          category: data.category,
        };

        this.qbStorageService.update(this.loadedQuickBuild.id, updatedQb).subscribe({
          next: (result) => {
            this.notify.success('Quick build updated');
            this.loadedQuickBuild = result;
          },
          error: () => {
            this.notify.error('Error updating quick build');
          },
        });
      }
    });
  }

  editQuickBuildMetadata() {
    if (!this.loadedQuickBuild) {
      return;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '500px';
    dialogConfig.data = {
      editMode: true,
      quickBuild: this.loadedQuickBuild,
    };

    const dialogRef = this.dialog.open(AddQuickBuildModalComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((data) => {
      if (data && this.loadedQuickBuild) {
        // Only update metadata, keep the current filters
        const updatedQb: StoredQuickBuild = {
          ...this.loadedQuickBuild,
          name: data.name,
          description: data.description,
          category: data.category,
          // Keep the existing rootFilterGroup (don't update filters)
          rootFilterGroup: this.loadedQuickBuild.rootFilterGroup,
        };

        this.qbStorageService.update(this.loadedQuickBuild.id, updatedQb).subscribe({
          next: (result) => {
            this.notify.success('Quick build details updated');
            // Update local reference but preserve current filters
            this.loadedQuickBuild = {
              ...result,
              rootFilterGroup: this.rootFilterGroup,
            };
            // Don't change isQuickBuildModified since we only updated metadata
          },
          error: () => {
            this.notify.error('Error updating quick build details');
          },
        });
      }
    });
  }

  /**
   * Helper methods
   */
  quickBuildFromChat(resp: ChatToQueryResult) {
    this.lastChatResult = resp;
    this.loadQuickBuild(resp.quickBuild, true);
  }

  onQuickBuildSelected(quickBuild: StoredQuickBuild | null): void {
    if (quickBuild) {
      this.loadQuickBuild(quickBuild);
      // Run search when user explicitly selects a quickbuild
      this.runSearch();
    }
    // Close the quickbuild menu programmatically
    if (this.quickBuildMenuTrigger) {
      this.quickBuildMenuTrigger.closeMenu();
    }
  }

  openQuickBuildBrowser(): void {
    const dialogRef = this.dialog.open(StoredItemsBrowserComponent, {
      maxWidth: '1200px',
      width: '95vw',
      height: '90vh',
      maxHeight: '1000px',
      backdropClass: 'tw-bg-black/80',

      data: {
        items$: this.qbStorageService.items$,
        config: this.qbStorageService.getBrowserConfig(),
      },
    });

    dialogRef.componentInstance.itemSelected.subscribe((quickBuild: StoredQuickBuild) => {
      this.loadQuickBuild(quickBuild);
      // Run search when user explicitly selects a quickbuild
      this.runSearch();
      dialogRef.close();
    });
  }

  loadQuickBuild(qb: QuickBuild, runSearch: boolean = false) {
    const rootFilterGroup = qb.rootFilterGroup;

    // recursively set the ids for each group
    const setIds = (group: UIFilterGroup) => {
      if (!group.id) {
        group.id = UniqueIdGenerator.generate();
      }
      group.filters.forEach((f) => {
        if (QuickBuildService.isUIFilterGroup(f)) {
          setIds(f);
        }
      });
    };
    setIds(rootFilterGroup);

    this.allReportIDsGrouped = QuickBuildService.getAllReportIDsInQuickbuildGrouped(qb);
    this.reportTarget = qb.rootFilterGroup.id || '';
    this.rootFilterGroup = rootFilterGroup || [];

    // Store the loaded quickbuild if it's a StoredQuickBuild with an ID
    if ('id' in qb && qb.id) {
      this.loadedQuickBuild = qb as StoredQuickBuild;
    } else {
      this.loadedQuickBuild = null;
    }

    // Only auto-search when coming from chat, not on page load
    if (runSearch) {
      this.runSearch();
    }
  }

  /**
   * Returns an array of match types as objects for the specified field.
   * @param field - The field for which to retrieve the match types.
   * @returns An array of match types as objects.
   */
  matchTypesAsObject(field: string): SearchFilter.MatchTypeEnum[] {
    let values = Object.values(SearchFilter.MatchTypeEnum);
    const col = this.columns.find((c) => c.name === field);

    // Date fields don't use `exact` match type
    if (col && col.type === ColumnType.date) {
      values = values.filter((v) => v !== SearchFilter.MatchTypeEnum.Exact);
    }

    // Enum and boolean fields don't use `fuzzy` match type
    if (
      col &&
      (col.type === ColumnType.jobfunction ||
        col.type === ColumnType.joblevel ||
        col.type === ColumnType.boolean)
    ) {
      values = values.filter((v) => v !== SearchFilter.MatchTypeEnum.Fuzzy);
    }

    // Company search defaults to exact
    if (col && col.type === ColumnType.companySearch) {
      values = [SearchFilter.MatchTypeEnum.Exact];
    }

    return values;
  }

  addBadgeToFilter(event: { filter: UIFilter }) {
    const filterIndex = this.uiFilters.findIndex((f) => f === event.filter);
    if (filterIndex !== -1) {
      this.uiFilters[filterIndex] = event.filter;
    }
  }

  removeBadgeFromFilter(event: { filter: UIFilter }) {
    const filterIndex = this.uiFilters.findIndex((f) => f === event.filter);
    if (filterIndex !== -1) {
      this.uiFilters[filterIndex] = event.filter;
    }
  }

  // FILTER CHIPS CODE
  toggleFiltersContent() {
    this.showFilters = !this.showFilters;
  }

  openInAdvancedEditor() {
    // Use the current setQuickBuild if available, otherwise create from current filters
    const quickBuild = this.setQuickBuild || {
      id: UniqueIdGenerator.generate(),
      name: 'Current Filters',
      rootFilterGroup: this.rootFilterGroup,
    };

    // Serialized (but not yet URL-encoded) quickbuild — the router encodes it when it goes
    // into the query params
    const serializedQuickBuild = QuickBuildService.serializeQuickBuild(quickBuild);

    let rootPage = '/people/search';
    if (!HostnameService.isWorkforceExperience()) {
      rootPage = '/' + this.authService.getSelectedOrgIdValue + '/people/filters';
    }

    // Oversized filters can't travel in the URL (see MAX_URL_FILTER_LENGTH) — hand them to the
    // new tab through localStorage, which the filters page falls back to when no querystring
    // filter is present
    if (encodeURIComponent(serializedQuickBuild).length > MAX_URL_FILTER_LENGTH) {
      localStorage.setItem('person-filter-params', serializedQuickBuild);
      window.open(this.router.createUrlTree([`${rootPage}`]).toString(), '_blank', 'noopener');
      return;
    }

    // Navigate to people/search with the quickbuild in query params
    const url = this.router.createUrlTree([`${rootPage}`], {
      queryParams: { settings: serializedQuickBuild },
    });

    // Open in new tab
    window.open(url.toString(), '_blank', 'noopener');
  }

  removeFilterChip(targetFilter: UIFilter) {
    this.removeFilterRecursive(this.rootFilterGroup, targetFilter);
  }

  private removeFilterRecursive(group: UIFilterGroup, targetFilter: UIFilter): boolean {
    for (let i = 0; i < group.filters.length; i++) {
      const filter = group.filters[i];

      if (QuickBuildService.isUIFilter(filter) && filter === targetFilter) {
        // Remove the target filter from the filters array
        group.filters.splice(i, 1);
        return true;
      } else if (QuickBuildService.isUIFilterGroup(filter) && filter?.filters) {
        // Recursively search in nested FilterGroups, ensure filter.filters is defined
        const found = this.removeFilterRecursive(filter, targetFilter);
        if (found) {
          // If the nested FilterGroup becomes empty after removal, optionally remove it
          if (filter.filters.length === 0) {
            group.filters.splice(i, 1);
          }
          return true;
        }
      }
    }
    return false;
  }

  get hasFilters(): boolean {
    return this.rootFilterGroup.filters.length > 0;
  }
  // END FILTER CHIPS CODE
}
